import type { ReactNode } from 'react';
import { Button } from '@mantine/core';
import { IconCloudOff, IconRefresh } from '@tabler/icons-react';
import { errorMessage } from '@/lib/copy';

export interface ErrorStateProps {
  /** Defaults to "Something went wrong". */
  title?: ReactNode;
  /** Plain message. If omitted, `error` is turned into a sentence (never a code). */
  message?: ReactNode;
  /** The thrown error, mapped with errorMessage() when `message` is not given. */
  error?: unknown;
  /** Shows a full-width "Try again" button. */
  onRetry?: () => void;
  retryLabel?: string;
  /** True while the retry is running (button shows a spinner). */
  retrying?: boolean;
  /** A way out, e.g. <Button component={Link} to="/home" variant="default">Back to Home</Button>. */
  secondaryAction?: ReactNode;
  /** Render without the card (inside another card). */
  plain?: boolean;
}

/**
 * Something failed to load: a plain message plus "Try again". Use it in place of the content
 * that failed, never a status that might be false (don't show "Required" when a request failed).
 */
export function ErrorState({ title = 'Something went wrong', message, error, onRetry, retryLabel = 'Try again', retrying, secondaryAction, plain }: ErrorStateProps) {
  const text = message ?? (error ? errorMessage(error) : 'Please check your internet connection and try again.');
  return (
    <div className={`rides-empty bad${plain ? '' : ' rides-card'}`} role="alert">
      <span className="ic" aria-hidden="true"><IconCloudOff size={28} /></span>
      <h2>{title}</h2>
      <div className="desc">{text}</div>
      {(onRetry || secondaryAction) && (
        <div className="acts">
          {onRetry && (
            <Button fullWidth onClick={onRetry} loading={retrying} leftSection={<IconRefresh size={18} />}>
              {retryLabel}
            </Button>
          )}
          {secondaryAction}
        </div>
      )}
    </div>
  );
}
